/**
 * The Captain roster panel model (M6 spec §5, §8 "Captains: roster"; plan P27): this station's candidates with
 * their skills, traits and wage, and the ship a hire goes to.
 *
 * - **Hiring** is free up front; the wage is paid per faction day from the wallet (sole trader) or the company
 *   account. It is refused once the hired pool is full (captains.json `capacity.hired`, `CaptainView.hiredCap`).
 * - **One captain per ship:** a hire can be assigned at once to an owned ship that has no captain (docked here
 *   first, then the rest in fleet order). A ship that already has a captain is never offered.
 *
 * Pure.
 */
import { SKILL_NAMES, type CaptainSummary, type CaptainTraitName, type FleetShipView } from '../../../strategic/types'
import { formatCr } from '../../format'
import { panelCatalog, type PanelCatalog } from './catalog'

export interface RosterInput {
  stationId: string
  roster: readonly CaptainSummary[]
  hired: readonly CaptainSummary[]
  hiredCap: number
  ships: readonly FleetShipView[]
  /** The ship the next hire is assigned to (a fleet id), or null for none. */
  assignTo: string | null
  soleTrader: boolean
}

export interface CandidateRow {
  /** The roster index the `hireCaptain` command takes. */
  candidate: number
  id: string
  name: string
  skills: string
  traits: string
  wage: number
  wageLabel: string
  canHire: boolean
  reason: string
}

export interface AssignTarget {
  shipId: string
  label: string
}

export interface RosterModel {
  rows: CandidateRow[]
  hiredLabel: string
  /** Captainless ships, docked here first. */
  targets: AssignTarget[]
  /** The valid selection (null when the chosen ship has a captain now or is gone). */
  assignTo: string | null
  assignLabel: string
  wageNote: string
  empty: string | null
}

const TRAIT_LABEL: Readonly<Record<CaptainTraitName, string>> = Object.freeze({
  cautious: 'Cautious',
  reckless: 'Reckless',
  greedy: 'Greedy',
  veteran: 'Veteran',
  exDescendant: 'Ex-Descendant',
  exMegacorp: 'Ex-Megacorp',
  unionist: 'Unionist',
  loyal: 'Loyal',
})

/** "PIL 3 · TRD 1 · …". */
export function skillsLine(c: CaptainSummary, cat: PanelCatalog = panelCatalog()): string {
  return SKILL_NAMES.map((s) => `${cat.config.skillShort[s]} ${c.skills[s]}`).join(' · ')
}

export function traitsLine(c: CaptainSummary): string {
  return c.traits.length === 0 ? 'No traits' : c.traits.map((t) => TRAIT_LABEL[t]).join(', ')
}

/** Ships a captain can take (one captain per ship): alive, without a captain; docked here first. */
export function assignTargets(ships: readonly FleetShipView[], stationId: string): FleetShipView[] {
  const free = ships.filter((s) => s.captain === null && s.status !== 'lost')
  const here = free.filter((s) => s.stationId === stationId)
  const rest = free.filter((s) => s.stationId !== stationId)
  return [...here, ...rest]
}

/** True when `shipId` can take a new captain now (it exists, is not lost and has none). */
export function canAssignTo(ships: readonly FleetShipView[], shipId: string): boolean {
  const s = ships.find((x) => x.id === shipId)
  return s !== undefined && s.status !== 'lost' && s.captain === null
}

/** The next assignment choice when cycling: none → each target in order → none. */
export function cycleAssign(targets: readonly AssignTarget[], current: string | null, delta: 1 | -1): string | null {
  const ids: (string | null)[] = [null, ...targets.map((t) => t.shipId)]
  const i = Math.max(0, ids.indexOf(current))
  const n = ids.length
  return ids[(i + delta + n) % n]
}

export function rosterModel(input: RosterInput, cat: PanelCatalog = panelCatalog()): RosterModel {
  const full = input.hired.length >= input.hiredCap
  const reason = full ? `Captain cap reached (${input.hired.length}/${input.hiredCap}).` : ''
  const rows = input.roster.map(
    (c, i): CandidateRow => ({
      candidate: i,
      id: c.id,
      name: c.name,
      skills: skillsLine(c, cat),
      traits: traitsLine(c),
      wage: c.wage,
      wageLabel: `${formatCr(Math.round(c.wage))}/day`,
      canHire: !full,
      reason,
    }),
  )
  const targets = assignTargets(input.ships, input.stationId).map(
    (s): AssignTarget => ({ shipId: s.id, label: `${s.name} (${cat.config.classLabels[s.shipClass]})${s.stationId === input.stationId ? ', here' : ''}` }),
  )
  const assignTo = input.assignTo !== null && targets.some((t) => t.shipId === input.assignTo) ? input.assignTo : null
  const chosen = targets.find((t) => t.shipId === assignTo)
  return {
    rows,
    hiredLabel: `Captains ${input.hired.length}/${input.hiredCap}`,
    targets,
    assignTo,
    assignLabel: chosen === undefined ? (targets.length === 0 ? 'No ship without a captain' : 'Unassigned') : chosen.label,
    wageNote: `Wages are paid each day from the ${input.soleTrader ? 'wallet' : 'company account'}.`,
    empty: rows.length === 0 ? 'No captains looking for work here today.' : null,
  }
}

/** The status line after a hire (and its assignment). */
export function hiredText(name: string, ship: string | null): string {
  return ship === null ? `${name} hired.` : `${name} hired to command the ${ship}.`
}
